import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { MessageCircleHeart, X, Send, Loader2, Languages, LifeBuoy, ArrowRightLeft, Copy, Check, Volume2, Square, Mic } from "lucide-react";
import { RecordingWaveform } from "./RecordingWaveform";

const LANG_BCP47 = { english: "en-US", shona: "sn-ZW", ndebele: "nd-ZW" };

const MODES = {
  shona: {
    key: "shona", label: "Translator", kind: "translate",
    title: "Translator", subtitle: "Translate between English, Shona & Ndebele",
    intro: "Type any text and I'll translate it. Pick the languages to translate from and to (English, Shona or Ndebele).",
    placeholder: "Type text to translate…", icon: Languages,
  },
  enquiry: {
    key: "enquiry", label: "General Enquiries", kind: "enquiry", endpoint: null, sidKey: "hutano_enquiry_gid",
    title: "General Enquiries", subtitle: "Message the Hutanohwevanhu team",
    intro: "Have a question for our team (bookings, services, feedback)? Send it here and our team will reply. You'll see their reply in this window.",
    placeholder: "Write your message to our team…", icon: LifeBuoy,
  },
};

function newGid() { return "gid-" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36); }

export function HealthAssistant() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("enquiry");
  const [threads, setThreads] = useState({ shona: [], enquiry: [] });
  const [sids, setSids] = useState({
    enquiry: localStorage.getItem(MODES.enquiry.sidKey) || "",
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const [srcLang, setSrcLang] = useState("auto");
  const [tgtLang, setTgtLang] = useState("shona");
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const ttsOk = typeof window !== "undefined" && "speechSynthesis" in window;
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [secsLeft, setSecsLeft] = useState(30);
  const [maxSecs, setMaxSecs] = useState(30);
  const [waveStream, setWaveStream] = useState(null);
  const limitRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const micApiOk = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window !== "undefined" && "MediaRecorder" in window;
  const [micOk, setMicOk] = useState(micApiOk);
  const [micRetrying, setMicRetrying] = useState(false);
  const [micRetryMsg, setMicRetryMsg] = useState("");
  const endRef = useRef(null);
  const { t } = useT();
  const cfg = MODES[mode];
  const msgs = threads[mode];

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [threads, busy, mode]);

  // Poll the team-reply unread count while the assistant is closed, so visitors
  // see when the team has responded even without opening the panel.
  useEffect(() => {
    const gid = sids.enquiry;
    if (!gid || open) return;
    let active = true;
    const loadUnread = async () => {
      try { const { data } = await api.get(`/enquiries/${gid}/unread`); if (active) setUnread(data.unread || 0); }
      catch { /* ignore */ }
    };
    loadUnread();
    const iv = setInterval(loadUnread, 12000);
    return () => { active = false; clearInterval(iv); };
  }, [open, sids.enquiry]);

  // "Chat with us" thread: load + poll for team replies while that tab is open.
  useEffect(() => {
    if (!open || mode !== "enquiry") return;
    const gid = sids.enquiry;
    if (!gid) return;
    let active = true;
    const loadThread = async () => {
      try {
        const { data } = await api.get(`/enquiries/${gid}`);
        if (!active) return;
        setThreads((t) => ({ ...t, enquiry: data.map((m) => ({ role: m.from_public ? "user" : "assistant", content: m.body, audioUrl: m.audio_url })) }));
        setUnread(0);
      } catch { /* ignore */ }
    };
    loadThread();
    const iv = setInterval(loadThread, 15000);
    return () => { active = false; clearInterval(iv); };
  }, [open, mode, sids.enquiry]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);

    if (cfg.kind === "translate") {
      const target = tgtLang;
      const source = srcLang === "auto" ? undefined : srcLang;
      setThreads((t) => ({ ...t, shona: [...t.shona, { role: "user", content: text }] }));
      try {
        const { data } = await api.post("/ai/translate", { text, target, source });
        setThreads((t) => ({ ...t, shona: [...t.shona, { role: "assistant", content: data.translation, lang: target }] }));
      } catch (e) {
        const detail = e?.response?.data?.detail || "Translation is unavailable right now. Please try again.";
        setThreads((t) => ({ ...t, shona: [...t.shona, { role: "assistant", content: detail, error: true }] }));
      } finally { setBusy(false); }
      return;
    }

    if (cfg.kind === "enquiry") {
      let gid = sids.enquiry || newGid();
      if (gid !== sids.enquiry) { setSids((s) => ({ ...s, enquiry: gid })); localStorage.setItem(MODES.enquiry.sidKey, gid); }
      setThreads((t) => ({ ...t, enquiry: [...t.enquiry, { role: "user", content: text }] }));
      try {
        await api.post("/enquiries", { guest_id: gid, body: text });
        const { data } = await api.get(`/enquiries/${gid}`);
        setThreads((t) => ({ ...t, enquiry: data.map((m) => ({ role: m.from_public ? "user" : "assistant", content: m.body, audioUrl: m.audio_url })) }));
      } catch (e) {
        const detail = e?.response?.data?.detail || "Couldn't send your message. Please try again.";
        setThreads((t) => ({ ...t, enquiry: [...t.enquiry, { role: "assistant", content: detail, error: true }] }));
      } finally { setBusy(false); }
      return;
    }

    setThreads((t) => ({ ...t, [mode]: [...t[mode], { role: "user", content: text }] }));
    try {
      const { data } = await api.post(cfg.endpoint, { message: text, session_id: sids[mode] || undefined });
      if (data.session_id && data.session_id !== sids[mode]) {
        setSids((s) => ({ ...s, [mode]: data.session_id }));
        localStorage.setItem(cfg.sidKey, data.session_id);
      }
      setThreads((t) => ({ ...t, [mode]: [...t[mode], { role: "assistant", content: data.reply }] }));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Sorry, the assistant is unavailable right now. Please try again.";
      setThreads((t) => ({ ...t, [mode]: [...t[mode], { role: "assistant", content: detail, error: true }] }));
    } finally { setBusy(false); }
  };

  const copyMsg = async (i, text) => {
    try { await navigator.clipboard.writeText(text); setCopiedIdx(i); setTimeout(() => setCopiedIdx((c) => (c === i ? null : c)), 1500); }
    catch { /* clipboard unavailable */ }
  };

  const speakMsg = (i, text, lang) => {
    if (!ttsOk) return;
    if (speakingIdx === i) { window.speechSynthesis.cancel(); setSpeakingIdx(null); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = LANG_BCP47[lang] || "en-US";
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx((c) => (c === i ? null : c));
    u.onerror = () => setSpeakingIdx((c) => (c === i ? null : c));
    setSpeakingIdx(i);
    window.speechSynthesis.speak(u);
  };

  useEffect(() => () => { if (ttsOk) window.speechSynthesis.cancel(); }, [ttsOk]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => { if (data?.voice_max_chat_seconds) setMaxSecs(data.voice_max_chat_seconds); }).catch(() => {});
  }, []);

  const stopTracks = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setWaveStream(null); };

  const retryMic = async () => {
    if (micRetrying) return;
    setMicRetrying(true);
    setMicRetryMsg("");
    if (!micApiOk) {
      setMicRetryMsg("This browser can't record audio. Try Chrome or Safari on a secure (https) connection.");
      setMicRetrying(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((tr) => tr.stop());
      setMicOk(true);
      setMicRetryMsg("");
    } catch {
      setMicRetryMsg("Still blocked. Allow microphone access in your browser's site settings, then try again.");
    } finally {
      setMicRetrying(false);
    }
  };

  const startRec = async () => {
    if (recording || transcribing) return;
    const recMode = mode;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopTracks();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) { setTranscribing(false); return; }
        setTranscribing(true);
        // General Enquiries: send the recording as a stored, playable voice note.
        if (recMode === "enquiry") {
          let gid = localStorage.getItem(MODES.enquiry.sidKey) || sids.enquiry || newGid();
          if (gid !== sids.enquiry) { setSids((s) => ({ ...s, enquiry: gid })); localStorage.setItem(MODES.enquiry.sidKey, gid); }
          setThreads((t) => ({ ...t, enquiry: [...t.enquiry, { role: "user", content: "🎤 Sending voice note…", pending: true }] }));
          try {
            const fd = new FormData();
            fd.append("guest_id", gid);
            fd.append("file", blob, "voice.webm");
            await api.post("/enquiries/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const { data } = await api.get(`/enquiries/${gid}`);
            setThreads((t) => ({ ...t, enquiry: data.map((m) => ({ role: m.from_public ? "user" : "assistant", content: m.body, audioUrl: m.audio_url })) }));
          } catch (e) {
            const detail = e?.response?.data?.detail || "Couldn't send your voice note. Please try again.";
            setThreads((t) => ({ ...t, enquiry: [...t.enquiry.filter((m) => !m.pending), { role: "assistant", content: detail, error: true }] }));
          } finally { setTranscribing(false); }
          return;
        }
        // Translator: transcribe to text and drop it into the input.
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) setInput((prev) => (prev ? prev + " " : "") + data.text);
        } catch { /* ignore transcription errors */ }
        finally { setTranscribing(false); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setWaveStream(stream);
      setSecsLeft(maxSecs);
      clearInterval(limitRef.current);
      limitRef.current = setInterval(() => setSecsLeft((s) => {
        if (s <= 1) { stopRec(); return 0; }
        return s - 1;
      }), 1000);
    } catch { setRecording(false); }
  };

  const stopRec = () => {
    clearInterval(limitRef.current);
    if (recRef.current && recRef.current.state === "recording") { recRef.current.stop(); }
    setRecording(false);
  };

  useEffect(() => () => { stopTracks(); clearInterval(limitRef.current); }, []);

  const footerNote = cfg.kind === "chat"
    ? "AI assistant · general info only, not a substitute for a clinician"
    : cfg.kind === "translate"
      ? "AI translation · English · Shona · Ndebele"
      : "Your message goes to our team — replies appear here";

  const Icon = cfg.icon;
  return (
    <>
      {!open && (
        <button onClick={() => { setOpen(true); }} data-testid="health-assistant-fab"
          className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-clinical text-white shadow-lg shadow-clinical/30 px-4 py-3 font-semibold hover:brightness-110 transition-[filter,transform] hover:scale-[1.03]">
          <MessageCircleHeart className="w-5 h-5" /> <span className="hidden sm:inline">{t("Chat with us")}</span>
          {unread > 0 && (
            <span data-testid="health-assistant-unread"
              className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-critical text-white text-xs font-bold">{unread}</span>
          )}
        </button>
      )}
      {open && (
        <div data-testid="health-assistant-panel"
          className="fixed bottom-5 right-5 z-[60] w-[92vw] max-w-[380px] h-[72vh] max-h-[580px] flex flex-col bg-white rounded-2xl shadow-2xl border border-line overflow-hidden">
          <div className="px-4 pt-3 pb-2 bg-clinical text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5" />
                <div className="leading-tight">
                  <p className="font-heading font-bold text-sm">{t(cfg.title)}</p>
                  <p className="text-[11px] text-white/80">{t(cfg.subtitle)}</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="health-assistant-close" className="hover:bg-white/15 rounded-full p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-1 mt-2.5 bg-white/15 rounded-full p-1">
              {Object.values(MODES).map((m) => (
                <button key={m.key} onClick={() => setMode(m.key)} data-testid={`assistant-mode-${m.key}`}
                  className={`flex-1 text-xs font-semibold rounded-full py-1.5 transition-colors ${mode === m.key ? "bg-white text-clinical" : "text-white/90 hover:bg-white/10"}`}>
                  {t(m.label)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-sand/40">
            {msgs.length === 0 && (
              <div className="text-sm text-muted2 bg-white border border-line rounded-lg p-3">{t(cfg.intro)}</div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={`assistant-msg-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-clinical text-white" : m.error ? "bg-critical/10 text-critical border border-critical/20" : "bg-white border border-line text-ink"}`}>
                {m.content}
                {m.audioUrl && (
                  <audio controls data-testid="enquiry-voice-audio"
                    src={`${process.env.REACT_APP_BACKEND_URL}${m.audioUrl}`}
                    className="mt-2 w-full h-9" />
                )}
                {cfg.kind === "translate" && m.role === "assistant" && !m.error && (
                  <div className="mt-1.5 flex items-center gap-3">
                    <button type="button" onClick={() => copyMsg(i, m.content)} data-testid="translation-copy-btn"
                      className="flex items-center gap-1 text-[11px] font-semibold text-clinical hover:opacity-80 transition-opacity">
                      {copiedIdx === i ? <><Check className="w-3 h-3" /> {t("Copied")}</> : <><Copy className="w-3 h-3" /> {t("Copy")}</>}
                    </button>
                    {ttsOk && (
                      <button type="button" onClick={() => speakMsg(i, m.content, m.lang)} data-testid="translation-speak-btn"
                        className="flex items-center gap-1 text-[11px] font-semibold text-clinical hover:opacity-80 transition-opacity">
                        {speakingIdx === i ? <><Square className="w-3 h-3" /> {t("Stop")}</> : <><Volume2 className="w-3 h-3" /> {t("Listen")}</>}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {busy && <div className="bg-white border border-line rounded-2xl px-3 py-2 inline-flex items-center gap-2 text-sm text-muted2"><Loader2 className="w-4 h-4 animate-spin" /> {cfg.kind === "translate" ? t("Translating…") : t("Thinking…")}</div>}
            <div ref={endRef} />
          </div>

          {cfg.kind === "translate" && (
            <div className="px-3 pt-2 bg-white flex items-center justify-center gap-2 text-xs font-semibold text-clinical">
              <select value={srcLang} onChange={(e) => setSrcLang(e.target.value)} data-testid="translator-source-lang"
                className="border border-clinical/30 rounded-full px-2.5 py-1.5 bg-white outline-none focus:border-clinical">
                <option value="auto">{t("Auto-detect")}</option>
                <option value="english">English</option>
                <option value="shona">Shona</option>
                <option value="ndebele">Ndebele</option>
              </select>
              <button type="button" onClick={() => { setSrcLang(tgtLang); setTgtLang(srcLang); }}
                data-testid="translator-swap" title="Swap languages" disabled={srcLang === "auto"}
                className="inline-flex items-center justify-center rounded-full border border-clinical/30 w-7 h-7 hover:bg-clinical/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <ArrowRightLeft className="w-3.5 h-3.5" />
              </button>
              <select value={tgtLang} onChange={(e) => setTgtLang(e.target.value)} data-testid="translator-target-lang"
                className="border border-clinical/30 rounded-full px-2.5 py-1.5 bg-white outline-none focus:border-clinical">
                <option value="english">English</option>
                <option value="shona">Shona</option>
                <option value="ndebele">Ndebele</option>
              </select>
            </div>
          )}

          <div className="p-2.5 border-t border-line bg-white">
            {recording && (
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-critical/30 bg-critical/5 px-2.5 py-1.5" data-testid="chat-recording-bar">
                <span className="inline-block w-2 h-2 rounded-full bg-critical animate-pulse" />
                <RecordingWaveform stream={waveStream} className="flex-1" />
                <span className="text-xs font-bold tabular-nums text-critical">{secsLeft}s</span>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea value={input} onChange={(e) => setInput(e.target.value)} data-testid="health-assistant-input" rows={1}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t(cfg.placeholder)} maxLength={1500}
                className="flex-1 resize-none rounded-xl border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-clinical/30 max-h-24" />
              {(cfg.kind === "translate" || cfg.kind === "enquiry") && micOk && (
                <button type="button" onClick={recording ? stopRec : startRec} disabled={transcribing}
                  data-testid="translator-mic-btn" title={recording ? `Stop (${secsLeft}s)` : "Speak"}
                  className={`rounded-xl p-2.5 border transition-colors disabled:opacity-40 min-w-[44px] ${recording ? "bg-critical text-white border-critical animate-pulse" : "bg-white text-clinical border-clinical/30 hover:bg-clinical/5"}`}>
                  {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <span data-testid="mic-countdown" className="text-xs font-bold tabular-nums">{secsLeft}s</span> : <Mic className="w-4 h-4" />}
                </button>
              )}
              {(cfg.kind === "translate" || cfg.kind === "enquiry") && !micOk && (
                <button type="button" disabled data-testid="mic-blocked-hint"
                  title="Microphone blocked — check browser/site permissions"
                  aria-label="Microphone blocked — check browser/site permissions"
                  className="rounded-xl p-2.5 border border-line bg-sand text-muted2 opacity-60 cursor-not-allowed min-w-[44px]">
                  <Mic className="w-4 h-4" />
                </button>
              )}
              <button onClick={send} disabled={busy || !input.trim()} data-testid="health-assistant-send"
                className="rounded-xl bg-clinical text-white p-2.5 disabled:opacity-40"><Send className="w-4 h-4" /></button>
            </div>
            {(cfg.kind === "translate" || cfg.kind === "enquiry") && !micOk && (
              <div className="mt-1.5 flex flex-col items-center gap-1 w-full" data-testid="mic-blocked-notice">
                <p className="text-[10px] text-critical text-center inline-flex items-center justify-center gap-1">
                  <Mic className="w-3 h-3" /> Microphone blocked — check your browser/site permissions to record voice notes
                </p>
                <button type="button" onClick={retryMic} disabled={micRetrying} data-testid="mic-retry-btn"
                  className="text-[10px] font-medium text-clinical border border-clinical/40 rounded-full px-3 py-1 hover:bg-clinical/5 disabled:opacity-50 inline-flex items-center gap-1">
                  {micRetrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />} Try again
                </button>
                {micRetryMsg && (
                  <p className="text-[10px] text-muted2 text-center" data-testid="mic-retry-msg">{micRetryMsg}</p>
                )}
              </div>
            )}
            <p className="text-[10px] text-muted2 mt-1.5 text-center">{t(footerNote)}</p>
          </div>
        </div>
      )}
    </>
  );
}
